






































































































































































































































































import { Component, Vue, PropSync, Prop, Watch } from "vue-property-decorator";
import store from "@/store";
import getCaretCoordinates from "textarea-caret";
// types
import { CustomExpressionData } from "@/types/CustomExpressionData";
import { ExtendedVessel } from "@/types/vessel";
import { LogVariable } from "@/types/logVariable";
import { FeatureAttribute } from "@/types/cds/featureAttribute";

// components
import LogVariablesDialog from "@/components/widgets/LogVariablesDialog.vue";
import CdsFeatureSelectDialog from "@/components/CdsFeatureSelectDialog.vue";

// modules
import { getModule } from "vuex-module-decorators";
import LogVariablesModule from "@/store/clients/LogVariables.module";
import VesselsModule from "@/store/clients/Vessels.module";
import CdsModule from "@/store/clients/Cds.module";

const LogVariables = getModule(LogVariablesModule, store);
const Vessels = getModule(VesselsModule, store);
const Cds = getModule(CdsModule, store);

@Component({
  components: {
    LogVariablesDialog,
    CdsFeatureSelectDialog,
  },
})
export default class MathematicalExpressionSelect extends Vue {
  @PropSync("expression", { required: true }) syncExpression!: CustomExpressionData;
  @Prop({ required: true }) vesselId!: number;
  @Prop({ default: false }) isLogicExpressionApplied!: boolean;
  @Prop({ default: false }) isModalActive!: boolean;

  // @Data
  vesselFeatureList: FeatureAttribute[] = [];
  mathContentArray: any[] = [];
  mathContent: string = "";
  showSuggestions = false;
  highlightedIndex: number = -1;
  filteredSuggestions: any[] = [];
  lastInsertedType: string | null = null;
  caretTop: number = 0;
  caretLeft: number = 0;
  search: string = "";
  operators = ["AND", "OR", "(", ")", "+", "-", "/", "*"];
  LogVariablesDialog = false;
  operator: string | null = null;
  symbol: string | null = null;
  amount = 0;
  conditionsList: { text: string; symbol: string }[] = [
    {
      text: "Greater than",
      symbol: ">",
    },
    {
      text: "Less than",
      symbol: "<",
    },
    {
      text: "Greater than OR equal to",
      symbol: ">=",
    },
    {
      text: "Less than OR equal to",
      symbol: "<=",
    },
    {
      text: "Equal to",
      symbol: "=",
    },
  ];
  logVariablesColumn: any = {
    logVariables: [],
  };

  // @Watchers
  @Watch("isModalActive")
  onDialogOpenChanged(val: boolean) {
    if (val) {
      // Dialog opened: re-initialize from syncExpression.content
      this.syncFromContent();
    }
  }
  @Watch("vesselLogVariables")
  onVesselLogVariablesLoaded() {
    this.syncFromContent();
  }

  @Watch("highlightedIndex")
  onHighlightedIndexChanged() {
    this.$nextTick(() => {
      let el = this.$refs.highlightedSuggestion;
      if (Array.isArray(el)) {
        el = el[el.length - 1];
      }
      if (el && "scrollIntoView" in el) {
        (el as HTMLElement).scrollIntoView({ block: "nearest" });
      }
    });
  }

  @Watch("logVariablesColumn", { deep: true })
  onLogVariablesColumnChanged(): void {
    this.onLogVariableAdd();
    this.$emit("onMathExpressionChanged");
  }

  // @Getters
  get isVesselFeaturesLogVariablesLoaded(): boolean {
    return Boolean(this.vesselFeaturesLogVariables);
  }

  get vesselFeaturesLogVariables(): LogVariable[] | FeatureAttribute[] | undefined {
    if (!this.useCdsFeatures) return LogVariables.logVariableByVesselId(this.vesselId) ?? [];
    return this.vesselFeatureList ?? [];
  }

  get selectedLogVariable(): LogVariable {
    return this.logVariablesColumn.logVariables[0] ?? ({} as LogVariable);
  }

  get selectedLogVariableName(): string {
    return this.selectedLogVariable?.name;
  }

  get selectedLogVariableID(): number {
    return this.selectedLogVariable.id;
  }

  get selectedLogVariableUnitName(): string {
    return this.selectedLogVariable?.unit?.name;
  }

  get vessel(): ExtendedVessel | undefined {
    return Vessels.getExtendedVesselById(this.vesselId);
  }

  get useCdsFeatures(): boolean {
    return this.vessel?.onboardSystem === "Nautilus";
  }

  get VesselFeatureLogVariableSelectType(): string {
    return this.vessel?.onboardSystem === "Nautilus" ? "cdsFeature" : "logVariable";
  }

  unitName(item: LogVariable | FeatureAttribute): string {
    return item.unit?.name || "N/A";
  }

  filterHeaders(suggestions: any[]): any[] {
    return suggestions.filter((item, idx, arr) => {
      if (!item.header) return true;
      // Find if there is at least one item of this group after the header
      const group = item.header;
      return arr.slice(idx + 1).some(next => next.group === group);
    });
  }

  get suggestionList(): { header?: string; name?: string; type?: string; unit?: string; group?: string }[] {
    const suggestions: { header?: string; name?: string; type?: string; unit?: string; group?: string }[] = [];
    suggestions.push({ header: "Log Variables" });
    this.vesselFeaturesLogVariables?.forEach(lv => {
      suggestions.push({ name: lv.name, type: "logVariable", unit: this.unitName(lv), group: "Log Variables" });
    });
    suggestions.push({ header: "Operators" });
    this.operators.forEach(op => {
      suggestions.push({ name: op, type: "operator", group: "Operators" });
    });
    suggestions.push({ header: "Symbols" });
    this.conditionsList.forEach(cond => {
      suggestions.push({ name: cond.symbol, type: "symbol", group: "Symbols" });
    });
    return suggestions;
  }

  // @Methods
  onInput(e: InputEvent): void {
    const value = (e.target as HTMLTextAreaElement).value;
    const currentWord = this.getCurrentWord(value).toLowerCase().trim();

    // Hide suggestions if textarea is empty
    if (!value.trim()) {
      this.showSuggestions = false;
      return;
    }

    // Only filter by search (current word), otherwise show all
    let filtered;
    if (!currentWord) {
      filtered = this.suggestionList;
    } else {
      filtered = this.suggestionList.filter(s => s.header || (s.name && s.name.toLowerCase().includes(currentWord)));
    }

    // Filter out headers with no items in their group
    this.filteredSuggestions = this.filterHeaders(filtered);

    this.showSuggestions = this.filteredSuggestions.length > 0;
    this.highlightedIndex = this.filteredSuggestions.findIndex(s => s.name);
    this.getCaretCoordinates();
  }

  onKeydown(e: KeyboardEvent): void {
    const textarea = this.$refs.mathTextarea as HTMLTextAreaElement;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;

    // Handle Backspace for deleting whole tokens or clearing all
    if (e.key === "Backspace") {
      // If all text is selected, clear everything
      if (start === 0 && end === this.mathContent.length && this.mathContent.length > 0) {
        this.mathContentArray = [];
        this.mathContent = "";
        this.buildMathStringsFromArray();
        this.$nextTick(() => {
          textarea.selectionStart = textarea.selectionEnd = 0;
        });
        e.preventDefault();
        return;
      }

      // If nothing is selected and cursor is at the end
      if (start === end && start > 0) {
        // Find the last token's end position
        let pos = 0;
        let lastTokenIndex = -1;
        for (let i = 0; i < this.mathContentArray.length; i++) {
          pos += this.mathContentArray[i].name.length + 1; // +1 for space
          if (pos >= start) {
            lastTokenIndex = i;
            break;
          }
        }
        // If the cursor is right after a token and that token is from the suggestion list, remove it
        if (
          lastTokenIndex === this.mathContentArray.length - 1 &&
          this.mathContentArray.length > 0 &&
          ["logVariable", "operator", "symbol", "number"].includes(this.mathContentArray[lastTokenIndex].type)
        ) {
          this.mathContentArray.pop();
          this.mathContent = this.mathContentArray.map(i => i.name).join(" ") + (this.mathContentArray.length ? " " : "");
          this.buildMathStringsFromArray();
          this.$nextTick(() => {
            textarea.selectionStart = textarea.selectionEnd = this.mathContent.length;
          });
          e.preventDefault();
          return;
        }
        // Otherwise, allow default backspace (removes a character)
      }
    }

    // Show suggestions if textarea is empty and user presses space
    if (e.key === " " && textarea.value.trim() === "") {
      this.filteredSuggestions = this.filterHeaders(this.suggestionList);
      this.showSuggestions = true;
      this.highlightedIndex = this.filteredSuggestions.findIndex(s => s.name);
      this.getCaretCoordinates();
      e.preventDefault();
      return;
    }

    // If user types space and suggestions are not open, add current word as a token
    if (e.key === " " && !this.showSuggestions) {
      const before = this.mathContent.slice(0, start);
      const word = this.getCurrentWord(before);
      if (word) {
        const arrayIndex = this.getMathContentArrayIndexAtCursor(start);
        const type = /^\d+(\.\d+)?$/.test(word) ? "number" : "unknown";
        this.mathContentArray.splice(arrayIndex, 0, { name: word, type });
        this.mathContent = this.mathContentArray.map(i => i.name).join(" ") + (this.mathContentArray.length ? " " : "");
        this.buildMathStringsFromArray();
        this.$nextTick(() => {
          textarea.selectionStart = textarea.selectionEnd = this.mathContent.length;
        });
        // Remove the just-typed word from the textarea (so it doesn't duplicate)
        e.preventDefault();
        return;
      }
    }

    if (!this.showSuggestions) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      let next = this.highlightedIndex;
      do {
        next++;
      } while (next < this.filteredSuggestions.length && !this.filteredSuggestions[next].name);
      if (next < this.filteredSuggestions.length) {
        this.highlightedIndex = next;
      }
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      let prev = this.highlightedIndex;
      do {
        prev--;
      } while (prev >= 0 && !this.filteredSuggestions[prev].name);
      if (prev >= 0) {
        this.highlightedIndex = prev;
      }
    } else if (e.key === "Enter" || e.key === " ") {
      if (this.showSuggestions && this.highlightedIndex >= 0 && this.highlightedIndex < this.filteredSuggestions.length) {
        const currentWord = this.getCurrentWord(this.mathContent);
        // Check if currentWord is a number and not an exact suggestion match
        const isNumber = /^\d+(\.\d+)?$/.test(currentWord);
        const exactMatch = this.filteredSuggestions.some(s => s.name && s.name.toLowerCase() === currentWord.toLowerCase());
        if (isNumber && !exactMatch) {
          // Add number as a token
          const arrayIndex = this.getMathContentArrayIndexAtCursor(start);
          this.mathContentArray.splice(arrayIndex, 0, { name: currentWord, type: "number" });
          this.mathContent = this.mathContentArray.map(i => i.name).join(" ") + (this.mathContentArray.length ? " " : "");
          this.buildMathStringsFromArray();
          this.$nextTick(() => {
            textarea.focus();
            const pos = this.mathContent.length;
            textarea.selectionStart = textarea.selectionEnd = pos;
            this.onInput({ target: textarea } as any);
            this.showSuggestions = true;
          });
          e.preventDefault();
          return;
        }
        // Otherwise, insert the suggestion as usual
        e.preventDefault();
        this.insertSuggestion(this.filteredSuggestions[this.highlightedIndex]);
      }
      // else: allow default Enter/Space behavior
    }
    this.getCaretCoordinates();
  }

  onTextareaBlur(): void {
    this.showSuggestions = false;
    // Parse the current textarea value into tokens (this will convert {id} to logVariable name in mathContentArray)
    this.syncArrayFromMathContent();
    // Always rebuild the textarea value from names (for display)
    this.mathContent = this.mathContentArray.map(i => i.name).join(" ") + (this.mathContentArray.length ? " " : "");
    // Always rebuild content from IDs (for saving)
    this.syncExpression.content = this.mathContentArray
      .map(token => {
        if (token.type === "logVariable") {
          // Find the ID for this log variable name
          const lv = this.vesselFeaturesLogVariables?.find(lv => lv.name === token.name);
          return lv ? `{${lv.id}}` : token.name;
        }
        return token.name;
      })
      .join(" ");
    // Optionally, also update mathContent with names for display
    this.syncExpression.mathContent = this.mathContent;
  }

  syncArrayFromMathContent() {
    // Split mathContent into tokens (numbers, operators, log variables, etc.)
    this.mathContentArray = this.parseContentToMathContentArray(this.mathContent.trim());
  }

  getMathContentArrayIndexAtCursor(cursorPos: number): number {
    let pos = 0;
    for (let i = 0; i < this.mathContentArray.length; i++) {
      pos += this.mathContentArray[i].name.length + 1; // +1 for space
      if (cursorPos <= pos) return i + 1;
    }
    return this.mathContentArray.length;
  }

  getCurrentWord(value: string): string {
    // If the last character is a space, there is no current word
    if (value.endsWith(" ")) return "";
    // Match the last word, operator, or symbol
    const match = value.match(/([a-zA-Z0-9_+\-*/()=<>]+)$/);
    return match ? match[1] : "";
  }

  insertSuggestion(item: any): void {
    const textarea = this.$refs.mathTextarea as HTMLTextAreaElement;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const before = this.mathContent.slice(0, start);
    const after = this.mathContent.slice(end);
    const word = this.getCurrentWord(before);

    // Find the array index at the cursor
    const arrayIndex = this.getMathContentArrayIndexAtCursor(start);

    // Insert into mathContentArray at the correct position
    this.mathContentArray.splice(arrayIndex, 0, item);

    // Rebuild mathContent from mathContentArray
    this.mathContent = this.mathContentArray.map(i => i.name).join(" ") + (this.mathContentArray.length ? " " : "");
    this.buildMathStringsFromArray();

    this.lastInsertedType = item.type;
    if (item.type === "operator" || item.type === "symbol" || item.name === ")") {
      this.lastInsertedType = null;
    }
    this.$nextTick(() => {
      textarea.focus();
      const pos = this.mathContent.length;
      textarea.selectionStart = textarea.selectionEnd = pos;
      this.onInput({ target: textarea } as any);
      this.showSuggestions = true;
    });
  }

  getCaretCoordinates(): void {
    const textarea = this.$refs.mathTextarea as HTMLTextAreaElement;
    if (!textarea) return;

    // Get caret coordinates relative to the textarea
    const coords = getCaretCoordinates(textarea, textarea.selectionEnd || 0);

    // Get textarea's position relative to the viewport
    const rect = textarea.getBoundingClientRect();

    // Calculate absolute position for the portal dropdown
    this.caretTop = rect.top + window.scrollY + coords.top + coords.height; // +height to go below the line
    this.caretLeft = rect.left + window.scrollX + coords.left;
  }

  buildMathStringsFromArray() {
    // Map logVariable names to IDs for quick lookup
    const logVarNameToId: Record<string, number> = {};
    this.vesselFeaturesLogVariables?.forEach(lv => {
      logVarNameToId[lv.name] = lv.id;
    });

    let contentParts: string[] = [];
    let mathContentParts: string[] = [];

    for (const token of this.mathContentArray) {
      if (token.type === "logVariable" && logVarNameToId[token.name]) {
        contentParts.push(`{${logVarNameToId[token.name]}}`);
        mathContentParts.push(token.name);
      } else if (token.type === "number") {
        contentParts.push(token.name);
        mathContentParts.push(token.name);
      } else if (token.type === "operator" || token.type === "symbol") {
        contentParts.push(token.name);
        mathContentParts.push(token.name);
      } else {
        // fallback for unknown types
        contentParts.push(token.name);
        mathContentParts.push(token.name);
      }
    }

    this.syncExpression.content = contentParts.join(" ");
    this.syncExpression.mathContent = mathContentParts.join(" ");
  }

  parseContentToMathContentArray(content: string): any[] {
    if (!content) return [];
    // Build a list of log variable names sorted by length (desc) for greedy matching
    const logVarNames = (this.vesselFeaturesLogVariables || []).map(lv => lv.name).sort((a, b) => b.length - a.length);

    const logVarIdToName: Record<string, string> = {};
    this.vesselFeaturesLogVariables?.forEach(lv => {
      logVarIdToName[lv.id] = lv.name;
    });

    // Tokenize by {id}, numbers, operators, symbols, or words
    const tokens = [];
    let str = content.trim();

    while (str.length > 0) {
      // 1. Match {12345}
      const idMatch = str.match(/^\{(\d+)\}/);
      if (idMatch && logVarIdToName[idMatch[1]]) {
        tokens.push({ name: logVarIdToName[idMatch[1]], type: "logVariable" });
        str = str.slice(idMatch[0].length).trimStart();
        continue;
      }

      // 2. Greedy match log variable names (multi-word)
      let matchedLogVar = null;
      for (const name of logVarNames) {
        if (str.startsWith(name)) {
          matchedLogVar = name;
          break;
        }
      }
      if (matchedLogVar) {
        tokens.push({ name: matchedLogVar, type: "logVariable" });
        str = str.slice(matchedLogVar.length).trimStart();
        continue;
      }

      // 3. Match number
      const numMatch = str.match(/^\d+(\.\d+)?/);
      if (numMatch) {
        tokens.push({ name: numMatch[0], type: "number" });
        str = str.slice(numMatch[0].length).trimStart();
        continue;
      }

      // 4. Match operator
      const op = this.operators.find(op => str.startsWith(op));
      if (op) {
        tokens.push({ name: op, type: "operator" });
        str = str.slice(op.length).trimStart();
        continue;
      }

      // 5. Match symbol
      const symbol = this.conditionsList.find(c => str.startsWith(c.symbol));
      if (symbol) {
        tokens.push({ name: symbol.symbol, type: "symbol" });
        str = str.slice(symbol.symbol.length).trimStart();
        continue;
      }

      // 6. Fallback: single word/char as unknown
      const fallback = str.match(/^\S+/);
      if (fallback) {
        tokens.push({ name: fallback[0], type: "unknown" });
        str = str.slice(fallback[0].length).trimStart();
        continue;
      }

      // Prevent infinite loop
      break;
    }

    return tokens;
  }

  syncFromContent() {
    // Initialize mathContentArray from syncExpression.content
    this.mathContentArray = this.parseContentToMathContentArray(this.syncExpression.content || "");
    // Rebuild mathContent string and mathContent
    this.mathContent = this.mathContentArray.map(i => i.name).join(" ") + (this.mathContentArray.length ? " " : "");
    this.buildMathStringsFromArray();
  }

  onFeatureSelected(feature: FeatureAttribute): void {
    this.logVariablesColumn.logVariables = [feature];
    Vue.set(this.syncExpression, "logVariable", feature);
    this.$emit("onMathExpressionChanged");
  }

  onOperatorAdd(): void {
    if (this.operator != null) {
      this.syncExpression.content += ` ${this.operator}`;
      this.syncExpression.mathContent += ` ${this.operator}`;
    }
    (this.$refs.MathematicalExpressionSelect as any).reset();
    (this.$refs.expressionAmount as any).internalValue = 0;
  }

  onLogVariableAdd(): void {
    this.syncExpression.content += ` {${this.selectedLogVariableID}}`;
    this.syncExpression.mathContent += ` ${this.selectedLogVariableName}`;
    (this.$refs.expressionAmount as any).internalValue = 0;
  }

  onSymbolAdd(): void {
    if (this.symbol != null) {
      this.syncExpression.content += ` ${this.symbol}`;
      this.syncExpression.mathContent += ` ${this.symbol}`;
    }
    (this.$refs.MathematicalExpressionSelect as any).reset();
    (this.$refs.expressionAmount as any).internalValue = 0;
  }

  onAmountAdd(): void {
    if (this.amount != null) {
      this.syncExpression.content += ` ${this.amount}`;
      this.syncExpression.mathContent += ` ${this.amount}`;
    }
    (this.$refs.MathematicalExpressionSelect as any).reset();
    (this.$refs.expressionAmount as any).internalValue = 0;
  }

  onAmountInput($value: any): void {
    if ($value === "") {
      $value = 0;
      (this.$refs.expressionAmount as any).internalValue = $value;
    }
  }

  onLogVariableInputClick(): void {
    this.LogVariablesDialog = true;
  }

  onLogVariableInputClear(): void {
    this.logVariablesColumn.logVariables = [] as LogVariable[];
  }

  numericFilter(event: any): void {
    event = event ? event : window.event;
    const expect = event.target.value.toString() + event.key.toString();
    return !/^\d+$/.test(expect) ? event.preventDefault() : false;
  }

  onClearMathResult(): void {
    this.syncExpression.content = "";
    this.syncExpression.amount = 0;
    this.syncExpression.logVariable = null;
    this.syncExpression.operator = null;
    this.syncExpression.symbol = "";
    setTimeout(() => {
      this.syncExpression.mathContent = "";
    }, 100);
  }

  async fetchFeaturesLogVariablesByVesselId(): Promise<void> {
    if (this.useCdsFeatures) {
      this.vesselFeatureList = await Cds.fetchVesselFeatures(this.vesselId);
    } else {
      if (!this.isVesselFeaturesLogVariablesLoaded && !this.vesselFeaturesLogVariables) await LogVariables.fetchAllLogVariablesByVesselId(this.vesselId);
    }
  }

  async created(): Promise<void> {
    await this.fetchFeaturesLogVariablesByVesselId();
    if (this.syncExpression.content == null || this.syncExpression.mathContent == null) {
      this.syncExpression.content = "";
      this.syncExpression.mathContent = "";
    }
    this.syncFromContent();
  }
}
